import React, { useEffect, useState } from "react";
import SelectCustom from "../../../../../../components/SelectCustom";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { IBumerangue } from "../../types/types";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface Option {
  value: number;
  label: string;
}

interface ITerminalResponse {
  id_terminal: number;
  razao_social: string;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IBumerangue;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = ({ isEdit, isView, selectedRow, onConfirm, onClose }: Props) => {
  const [terminalOptions, setTerminalOptions] = useState<Option[]>([]);
  const [terminalId, setTerminalId] = useState<number | undefined>(selectedRow?.id_terminal);
  const [intervaloDias, setIntervaloDias] = useState<string>(
    selectedRow ? String(selectedRow.intervalo_dias ?? "") : ""
  );
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isReadOnly = Boolean(isView);

  useEffect(() => {
    const fetchTerminals = async () => {
      try {
        const body = {
          qtd_por_pagina: 200,
          order_by: "razao_social",
          order_direction: "asc",
        };

        const response = await api.post("/listar/terminais", body);
        const mappedOptions = response.data.data.map((terminal: ITerminalResponse) => ({
          value: terminal.id_terminal,
          label: terminal.razao_social,
        }));
        setTerminalOptions(mappedOptions);
      } catch (error) {
        console.error("Erro ao buscar terminais:", error);
        FrontendNotification("Erro ao carregar lista de terminais", "error");
      }
    };

    fetchTerminals();
  }, []);

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();

    if (!terminalId) {
      FrontendNotification("Selecione um terminal", "warning");
      return;
    }

    const intervaloValue = Math.floor(Number(intervaloDias));

    if (!intervaloDias || intervaloValue <= 0 || Number.isNaN(intervaloValue)) {
      FrontendNotification("Informe o intervalo em dias", "warning");
      return;
    }

    const payload = {
      id_terminal: Number(terminalId),
      intervalo_dias: intervaloValue,
      id_usuario_historico: user?.id,
    };

    try {
      setLoading(true);

      if (isEdit && selectedRow?.id_bumerangue) {
        await api.post("/editar/comercialBumerangue", {
          ...payload,
          id_comercial_bumerangue: selectedRow.id_bumerangue,
        });
      } else {
        await api.post("/cadastrar/comercialBumerangue", payload);
      }

      FrontendNotification(
        `Registro ${isEdit ? "atualizado" : "cadastrado"} com sucesso`,
        "success"
      );
      onConfirm();
      onClose();
    } catch (error: unknown) {
      console.error("Erro ao salvar bumerangue:", error);
      const axiosError = error as { response?: { data?: Record<string, unknown> } };
      const responseData = axiosError?.response?.data;
      let errorMessage = "Erro ao salvar Bumerangue";

      const errors = responseData?.errors as Record<string, string[]> | undefined;

      if (errors) {
        const firstKey = Object.keys(errors)[0];
        const firstMessage = firstKey ? errors[firstKey]?.[0] : undefined;
        if (typeof firstMessage === "string") {
          errorMessage = firstMessage;
        }
      } else if (typeof responseData?.error_msg === "string") {
        errorMessage = responseData.error_msg;
      }

      FrontendNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col col-span-2">
          <SelectCustom
            title="Terminal"
            data={terminalOptions}
            onChange={(selected: Option | null) =>
              setTerminalId(selected ? Number(selected.value) : undefined)
            }
            value={terminalId}
            disabled={isReadOnly}
          />
        </label>

        <label className="flex flex-col">
          <span className="text-sm mb-1">Intervalo em dias</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={intervaloDias}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const val = event.target.value;
              if (/^\d*$/.test(val)) {
                setIntervaloDias(val);
              }
            }}
            className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]"
            disabled={isReadOnly}
            required
          />
        </label>
      </div>

      <div className="flex justify-end mt-6 gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 rounded-full border border-[#062D4E] text-[#062D4E] bg-white hover:bg-[#F0F4F8] transition"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 rounded-full bg-[#062D4E] text-white hover:bg-[#094077] transition"
        >
          {loading ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </form>
  );
};

export default Form;
